import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Send, Trophy, HelpCircle, Loader2, Gamepad2 } from 'lucide-react';
import { getTenant } from '../config/tenants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// WHITE-LABEL PLAYER CHAT - Clean, no demo banners
export function ArenaPlayerChat() {
  const tenant = getTenant('arena');
  const [messages, setMessages] = useState([
    { role: 'assistant', content: tenant.welcomeMessage }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/chat/message`, { message: userMessage, context: 'tournament' });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, suggestions: data.suggestions }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I'm having trouble right now. Please try again!", error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { icon: Trophy, label: "Find Tournaments", query: "Show me upcoming tournaments" },
    { icon: Gamepad2, label: "Popular Games", query: "What games have tournaments?" },
    { icon: HelpCircle, label: "Get Help", query: "I need help with my account" },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(135deg, ${tenant.colors.background} 0%, #1a2744 100%)` }}>
      <div className="w-full max-w-md">
        <div className="rounded-2xl shadow-2xl overflow-hidden" style={{ background: tenant.colors.surface, border: `1px solid ${tenant.colors.border}` }}>
          {/* Header */}
          <div className="p-4" style={{ background: `linear-gradient(135deg, ${tenant.colors.primary} 0%, ${tenant.colors.primaryHover} 100%)` }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center p-1">
                <img src={tenant.logo} alt={tenant.name} className="w-full h-full rounded-full object-contain" />
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-white text-lg">{tenant.agentName}</h2>
                <p className="text-white/80 text-sm">{tenant.tagline}</p>
              </div>
              <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="h-96 overflow-y-auto p-4 space-y-4" style={{ scrollBehavior: 'smooth' }}>
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div 
                  className={`max-w-[85%] px-4 py-3 ${msg.role === 'user' ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-bl-md'}`}
                  style={{ background: msg.role === 'user' ? tenant.colors.primary : tenant.colors.surfaceHover, color: tenant.colors.text }}
                >
                  <p className="text-sm leading-relaxed">{msg.content}</p>
                  {msg.suggestions && msg.suggestions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {msg.suggestions.map((sug, sidx) => (
                        <button key={sidx} onClick={() => setInput(sug)} className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors" style={{ color: 'rgba(255,255,255,0.9)' }}>
                          {sug}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-md px-4 py-3" style={{ background: tenant.colors.surfaceHover }}>
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: tenant.colors.primary }} />
                    <span className="text-sm" style={{ color: tenant.colors.textMuted }}>Typing...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2">
              <div className="flex gap-2">
                {quickActions.map((action, idx) => (
                  <button key={idx} onClick={() => setInput(action.query)} className="flex-1 flex items-center justify-center gap-2 py-2 px-2 rounded-lg transition-colors" style={{ background: tenant.colors.surfaceHover, border: `1px solid ${tenant.colors.border}` }}>
                    <action.icon className="w-4 h-4" style={{ color: tenant.colors.primary }} />
                    <span className="text-xs" style={{ color: tenant.colors.textMuted }}>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4" style={{ borderTop: `1px solid ${tenant.colors.border}` }}>
            <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 rounded-full px-4 py-2.5 text-sm focus:outline-none"
                style={{ background: tenant.colors.surfaceHover, border: `1px solid ${tenant.colors.border}`, color: tenant.colors.text }}
                disabled={loading}
              />
              <button type="submit" disabled={loading || !input.trim()} className="w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:opacity-50" style={{ background: tenant.colors.primary }}>
                <Send className="w-4 h-4 text-white" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
